import posthog from "posthog-js";

/**
 * Product analytics (PostHog).
 *
 * Configuration comes from CRA env vars:
 *   REACT_APP_POSTHOG_KEY   – project API key (required to enable analytics)
 *   REACT_APP_POSTHOG_HOST  – ingestion host (optional, defaults to US cloud)
 *
 * If no key is configured (e.g. local dev), every function here is a safe no-op,
 * so the app behaves identically with analytics disabled.
 */

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let enabled = false;

export function initAnalytics(): void {
  if (!KEY) return;
  posthog.init(KEY, {
    api_host: HOST,
    // We capture SPA pageviews manually on route change (see PageViewTracker),
    // so disable the automatic one that only fires on hard load.
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    persistence: "localStorage+cookie",
  });
  enabled = true;
}

export interface IdentifyTraits {
  email?: string;
  name?: string;
  subscriptionPaid?: boolean;
}

/** Tie subsequent events to a known user. Call after successful auth. */
export function identifyUser(id: string, traits?: IdentifyTraits): void {
  if (!enabled) return;
  posthog.identify(id, traits);
}

/** Clear the identified user. Call on logout. */
export function resetUser(): void {
  if (!enabled) return;
  posthog.reset();
}

/** Manual SPA pageview. Called on every route change. */
export function trackPageView(path: string): void {
  if (!enabled) return;
  posthog.capture("$pageview", { path });
}

/**
 * Central catalog of product events. Add new event names here so they stay
 * consistent across the app and easy to find. Keep names snake_case.
 */
export const EVENTS = {
  MAP_VIEW_SELECTED: "map_view_selected",
  PRESCRIPTION_GENERATED: "prescription_generated",
  PRESCRIPTION_EXPORTED: "prescription_exported",
  TRACKS_EXPORTED: "tracks_exported",
  HARVEST_REPORT_GENERATED: "harvest_report_generated",
  AGENT_MESSAGE_SENT: "agent_message_sent",
  /**
   * The app decided the user is no longer signed in. `reason` says who decided:
   * `401` (a backend call answered 401 mid-session; `path` is the endpoint),
   * `boot-null` (page load, get-session answered "no session"), `boot-error`
   * (page load, get-session itself failed even after a retry — `status` is the
   * HTTP status, or null for a network error). Added after a 2026-09-03 logout
   * that PostHog could only show as "/fields, then /login".
   */
  SESSION_LOST: "session_lost",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/** Fire a product event. */
export function track(
  event: EventName | string,
  properties?: Record<string, unknown>
): void {
  if (!enabled) return;
  posthog.capture(event, properties);
}
